/**
 * Transfer wizard tab navigation and derived UI state.
 */
import { useCallback, useMemo } from 'react'
import type { EntityEditTabItem } from '@/components/ui/EntityEditTabBar'
import type { TransferTabId } from '../types'
import {
  computeTransferNextDisabled,
  employeeTransferStepIsVacant,
  employeeTabDisabledFromProperty,
  showPaymentsSection,
  transferFirstStepLabel,
  transferWizardTabOrder,
} from '../utils/transferPropertyCompanyWizard'
import type { TransferProgressFlagOverrides } from '../utils/transferProcessProgressPayload'
import type { TransferProcessWorkflowDeps } from './transferWorkflowTypes'

type ActionApi = {
  completeStaffTransfer: () => Promise<void>
  completeSplitPropertyTransfer: () => Promise<void>
}

type ProgressApi = {
  saveTransferProgress: (flagOverrides?: TransferProgressFlagOverrides) => Promise<void>
}

export function useTransferProcessWizardNav(
  { routing, dialogs, form, core }: TransferProcessWorkflowDeps,
  actions: ActionApi,
  progress: ProgressApi,
) {
  const { navigate, fragment, entityId } = routing
  const { openFlowConfirm } = dialogs
  const { formState, fkCompanyId, newPropertyName } = form
  const {
    selectedTab,
    setSelectedTab,
    selectedSubTab,
    infoDone,
    empDone,
    residentDone,
    commitDone,
    verifyDone,
    staffList,
    removestaffList,
    transferstaffList,
    addedstaffList,
    residents,
    applications,
    residentcount,
    applicantcount,
    setInfoDone,
    setResidentDone,
    setEmpDone,
    setVerifyDone,
  } = core

  const { completeStaffTransfer, completeSplitPropertyTransfer } = actions
  const { saveTransferProgress } = progress

  const showPayments = showPaymentsSection(fragment)
  const showEmployeeTab = fragment !== 'transfer_resident'
  const tabOrder: TransferTabId[] = transferWizardTabOrder(showEmployeeTab)
  const currentTabIndex = tabOrder.indexOf(selectedTab)
  const nextDisabled = computeTransferNextDisabled({
    selectedTab,
    selectedSubTab,
    formIsValid: formState.isValid,
    fragment,
    newPropertyNameTrimmed: String(newPropertyName || '').trim(),
    commitDone,
    verifyDone,
    removestaffListLength: removestaffList.length,
    transferstaffListLength: transferstaffList.length,
    staffListLength: staffList.length,
    addedstaffListLength: addedstaffList.length,
    residentsLength: residents.length,
    applicationsLength: applications.length,
    residentcount,
    applicantcount,
  })

  const transferEntityTabs = useMemo((): EntityEditTabItem[] => {
    const employeeStepNonClickableFromProperty = employeeTabDisabledFromProperty({
      selectedTab,
      fkCompanyId: String(fkCompanyId ?? ''),
      fragment,
      infoDone,
    })
    const item = (id: TransferTabId, label: string): EntityEditTabItem => {
      const complete =
        id === 'propertyinfo'
          ? infoDone
          : id === 'contactsinfo'
            ? empDone
            : id === 'residentinfo'
              ? residentDone
              : residentDone
      const disabled =
        (id === 'contactsinfo' && employeeStepNonClickableFromProperty) ||
        (id === 'residentinfo' &&
          (showEmployeeTab ? !empDone : !infoDone)) ||
        (id === 'summaryinfo' && !residentDone)
      return {
        id,
        label,
        testId: `transfer-process-step-button-${id}`,
        complete,
        disabled,
      }
    }
    const out: EntityEditTabItem[] = [item('propertyinfo', transferFirstStepLabel(fragment))]
    if (showEmployeeTab) out.push(item('contactsinfo', 'Employee'))
    out.push(item('residentinfo', 'Residents'), item('summaryinfo', 'Summary'))
    return out
  }, [showEmployeeTab, selectedTab, fkCompanyId, fragment, infoDone, empDone, residentDone])

  const previousTab = useCallback(() => {
    const order = tabOrder
    const idx = order.indexOf(selectedTab)
    if (idx > 0) {
      setSelectedTab(order[idx - 1] ?? 'propertyinfo')
    }
  }, [tabOrder, selectedTab, setSelectedTab])

  const nextTab = useCallback(async () => {
    const order = tabOrder
    const currentIdx = order.indexOf(selectedTab)
    if (currentIdx < 0) return
    if (currentIdx >= order.length - 1) {
      openFlowConfirm({
        title: 'Exit wizard?',
        message: 'Do you have another transfer to make?',
        onProceed: () => {
          navigate(fragment === 'transfer_company' ? '/company' : '/property')
        },
        onCancel: () => {
          if (fragment === 'transfer_company') {
            navigate(`/company/edit/${entityId}`)
          } else if (entityId) {
            navigate(`/property/edit/${entityId}`)
          } else {
            navigate('/dashboard')
          }
        },
      })
      return
    }
    const nextTabId = order[currentIdx + 1] ?? 'summaryinfo'

    if (selectedTab === 'propertyinfo' && formState.isValid) {
      if (fragment === 'split_property') {
        await completeSplitPropertyTransfer()
        return
      }
      setInfoDone(true)
    }
    if (selectedTab === 'residentinfo') {
      // Angular `next()`: split_property Finish also commits via completeSplitPropertyTransfer.
      if (fragment === 'split_property') {
        await completeSplitPropertyTransfer()
        return
      }
      // Angular: set residentDone, bump index, save (fire-and-forget), then selectedTab = summary.
      // Advance tab first so UI cannot flash Property Info if anything remounts mid-save.
      setResidentDone(true)
      setSelectedTab(nextTabId)
      void saveTransferProgress({ residentDone: true })
      return
    }
    if (selectedTab === 'contactsinfo') {
      if (commitDone && verifyDone) setEmpDone(true)
    }
    await saveTransferProgress()
    const employeeStepVacant = employeeTransferStepIsVacant({
      staffListLength: staffList.length,
      removestaffListLength: removestaffList.length,
      transferstaffListLength: transferstaffList.length,
    })
    if (selectedTab === 'contactsinfo' && employeeStepVacant) {
      if (selectedSubTab === 'transferstaff') {
        // Split must POST /api/split_property; completeStaffTransfer skips that and still sets commitDone.
        if (fragment === 'split_property') {
          await completeSplitPropertyTransfer()
        } else {
          await completeStaffTransfer()
        }
        return
      }
      if (selectedSubTab === 'addstaff') {
        // Angular auto-sets verifyDone when Add & Verify lists are empty
        setVerifyDone(true)
        setEmpDone(true)
      }
      setSelectedTab(nextTabId)
    } else {
      setSelectedTab(nextTabId)
    }
  }, [
    tabOrder,
    selectedTab,
    openFlowConfirm,
    navigate,
    fragment,
    entityId,
    formState.isValid,
    completeSplitPropertyTransfer,
    setInfoDone,
    setResidentDone,
    commitDone,
    verifyDone,
    setEmpDone,
    saveTransferProgress,
    staffList.length,
    removestaffList.length,
    transferstaffList.length,
    selectedSubTab,
    completeStaffTransfer,
    setVerifyDone,
    setSelectedTab,
  ])

  return {
    showPayments,
    showEmployeeTab,
    tabOrder,
    currentTabIndex,
    nextDisabled,
    transferEntityTabs,
    previousTab,
    nextTab,
  }
}
